import { Timestamp } from 'firebase/firestore';
import { GameType, TableStatus } from './Table';

// ===== TABLE TEMPLATES =====

export interface TableTemplate {
  id: string;
  room_id: string;
  name: string;                   // Template name (also default table name when used)
  game_type: GameType;
  custom_game_type?: string | undefined;
  stakes: string;
  number_of_tables: number;
  status: TableStatus;
  waitlist_limit?: number | undefined;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface CreateTemplateData {
  name: string;
  game_type: GameType;
  custom_game_type?: string | undefined;
  stakes: string;
  number_of_tables: number;
  status: TableStatus;
  waitlist_limit?: number | undefined;
}

export interface UpdateTemplateData {
  name?: string | undefined;
  game_type?: GameType | undefined;
  custom_game_type?: string | undefined;
  stakes?: string | undefined;
  number_of_tables?: number | undefined;
  status?: TableStatus | undefined;
  waitlist_limit?: number | undefined;
}

// ===== RECURRENCE =====

export type ScheduleFrequency = 'once' | 'daily' | 'weekly';

export type EndCondition = 'never' | 'date' | 'occurrences';

export interface RecurrenceRule {
  frequency: 'daily' | 'weekly';
  days_of_week?: number[] | undefined;   // For weekly: 0=Sun, 1=Mon, ..., 6=Sat
  time_of_day: string;                   // Local time "HH:mm" e.g. "18:00"
  start_date: string;                    // "YYYY-MM-DD"
  end_condition: EndCondition;
  end_date?: string | undefined;         // "YYYY-MM-DD" (when end_condition === 'date')
  max_occurrences?: number | undefined;  // (when end_condition === 'occurrences')
}

// ===== SCHEDULED TABLES =====

export type ScheduledTableStatus = 'scheduled' | 'executed' | 'failed' | 'cancelled' | 'completed';

/** Snapshot of table data stored on each schedule — editable without changing the template */
export interface ScheduledTableData {
  name: string;
  game_type: GameType;
  custom_game_type?: string | undefined;
  stakes: string;
  number_of_tables: number;
  status: TableStatus;
  waitlist_limit?: number | undefined;
}

export interface ScheduledTable {
  id: string;
  room_id: string;
  template_id: string;            // Reference to source template (for audit trail)
  table_data: ScheduledTableData; // Editable snapshot — this is what gets created

  // Schedule type
  type: ScheduleFrequency;        // 'once' | 'daily' | 'weekly'

  // The engine always queries on this single field:
  next_run_time_utc: Timestamp;
  timezone: string;               // e.g., "America/Los_Angeles"

  // Recurrence (only for 'daily' | 'weekly')
  recurrence_rule?: RecurrenceRule | undefined;
  occurrences_count: number;      // How many times this schedule has fired

  status: ScheduledTableStatus;
  created_by: string;             // Admin UID
  created_at: Timestamp;
  updated_at: Timestamp;
  executed_at?: Timestamp | undefined;   // Last execution time
  error_message?: string | undefined;
}

export const SCHEDULED_TABLE_STATUSES: ScheduledTableStatus[] = [
  'scheduled',
  'executed',
  'completed',
  'failed',
  'cancelled',
];

export const SCHEDULE_FREQUENCIES: { value: ScheduleFrequency; label: string }[] = [
  { value: 'once', label: 'Once' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
